import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0, n2 } from "../../lib/format";
import { LineChart } from "../../lib/charts";
import { addDays, iso, monday, monthEnd, monthStart } from "../../lib/dates";
import DataTable from "../../lib/DataTable";
import { Modal, Notice, Stat } from "../../lib/ui";

/*
 * Sales analytics – port of sales.php (API: /api/sales/analytics, POST /api/sales/<id>)
 * Presets or a custom range, KPIs, revenue & profit over time, top products and manufacturers, every sale
 * (CSV export, print). Admins can correct a sale's quantity, price, total and profit.
 */

const now = new Date();
const q0 = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
const lastMonth = addDays(monthStart(now), -1);
const PRESETS = {
  today: ["Today", iso(now), iso(now)], yesterday: ["Yesterday", iso(addDays(now, -1)), iso(addDays(now, -1))],
  this_week: ["This week", iso(monday(now)), iso(now)], last_week: ["Last week", iso(addDays(monday(now), -7)), iso(addDays(monday(now), -1))],
  this_month: ["This month", iso(monthStart(now)), iso(now)], last_month: ["Last month", iso(monthStart(lastMonth)), iso(monthEnd(lastMonth))],
  this_quarter: ["This quarter", iso(q0), iso(now)], this_year: ["This year", `${now.getFullYear()}-01-01`, iso(now)],
};

export default function SalesAnalytics({ user }) {
  const api = useApi();
  const [f, setF] = useState({ start_date: iso(monthStart(now)), end_date: iso(now), search: "" });
  const [form, setForm] = useState(f);
  const [preset, setPreset] = useState("this_month");
  const [d, setD] = useState(null);
  const [notice, setNotice] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async (q) => { try { setD(await api.get("/api/sales/analytics", q)); } catch (e) { setNotice({ tone: "err", text: e.message }); } }, [api]);
  useEffect(() => { load(f); }, [load, f]);

  const applyPreset = (k) => { const [, s, e] = PRESETS[k]; setPreset(k); setForm({ ...form, start_date: s, end_date: e }); setF({ ...f, start_date: s, end_date: e }); };
  const exportCsv = () => {
    const head = ["Sale ID", "Product", "Brand", "Date", "Price", "Qty", "Total", "Profit", "Reference"];
    const lines = d.rows.map((r) => [r.sale_id, r.product_name, r.manufacturer, r.sale_date, r.sale_price, r.quantity_sold, r.total_amount, r.profit, r.transaction_reference]);
    const csv = [head, ...lines].map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
    a.download = `sales_${f.start_date}_${f.end_date}.csv`; a.click();
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try { const r = await api.post(`/api/sales/${edit.sale_id}`, edit); setNotice({ tone: "ok", text: r.message }); setEdit(null); load(f); }
    catch (err) { setEdit((x) => ({ ...x, error: err.message })); }
  };

  const s = d?.stats;
  const admin = user?.role === "admin";
  const columns = [
    { key: "sale_id", label: "Sale ID", num: true }, { key: "product_name", label: "Product", render: (r) => <b style={{ color: "var(--ink)" }}>{r.product_name}</b> },
    { key: "manufacturer", label: "Brand" }, { key: "sale_date", label: "Date" },
    { key: "sale_price", label: "Price", num: true, render: (r) => n2(r.sale_price) }, { key: "quantity_sold", label: "Qty", num: true, render: (r) => n0(r.quantity_sold) },
    { key: "total_amount", label: "Total", num: true, render: (r) => n2(r.total_amount) },
    { key: "profit", label: "Profit", num: true, render: (r) => <span style={{ color: r.profit < 0 ? "var(--danger)" : undefined }}>{n2(r.profit)}</span> },
    { key: "transaction_reference", label: "Reference" },
    ...(admin ? [{ key: "edit", label: "", sortable: false, render: (r) => <button type="button" className="btn sm" onClick={() => setEdit({ ...r })}>Edit</button> }] : []),
  ];

  return (
    <main className="pg" style={{ maxWidth: "92rem" }}>
      <PageHeader slug="sales" actions={<><button type="button" className="btn" onClick={() => window.print()}><i className="fas fa-print" /> Print</button>
        <button type="button" className="btn" disabled={!d?.rows.length} onClick={exportCsv}><i className="fas fa-file-csv" /> Export CSV</button></>} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <section className="panel no-print" style={{ marginBottom: "1.25rem" }}>
        <div className="presets">{Object.entries(PRESETS).map(([k, [label]]) => <button key={k} type="button" aria-pressed={preset === k} onClick={() => applyPreset(k)}>{label}</button>)}</div>
        <form className="toolbar" style={{ borderBottom: 0 }} onSubmit={(e) => { e.preventDefault(); setPreset(""); setF({ ...form }); }}>
          <div className="field"><label htmlFor="sa-s">From</label><input id="sa-s" type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
          <div className="field"><label htmlFor="sa-e">To</label><input id="sa-e" type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          <div className="field grow"><label htmlFor="sa-q">Search</label><input id="sa-q" value={form.search} onChange={(e) => setForm({ ...form, search: e.target.value })} placeholder="Sale ID, product or reference" /></div>
          <button type="submit" className="btn primary">Apply</button>
        </form>
      </section>
      {s && (
        <div className="ui-stats">
          <Stat icon="fa-coins" tone="up" label={`Revenue · ${n0(s.total_sales)} sales`} value={n0(s.total_revenue)} unit="TZS" />
          <Stat icon="fa-sack-dollar" label={`Profit · margin ${s.profit_margin.toFixed(1)}%`} value={n0(s.total_profit)} unit="TZS" />
          <Stat icon="fa-cubes" tone="muted" label="Units sold" value={n0(s.total_quantity)} />
          <Stat icon="fa-receipt" tone="warn" label={`Average sale · profit ${n0(s.avg_profit_per_sale)}`} value={n0(s.avg_sale_value)} unit="TZS" />
          <Stat icon="fa-calendar-day" tone="muted" label={`Per day · ${s.avg_daily_sales.toFixed(1)} sales`} value={n0(s.avg_daily_revenue)} unit="TZS" />
        </div>
      )}
      {d && (
        <>
          <section className="panel card-pad" style={{ marginBottom: "1.25rem" }}>
            <h3>Revenue and profit over time <small style={{ color: "var(--muted)", fontWeight: 400 }}>{d.chart.length} day{d.chart.length === 1 ? "" : "s"} with data</small></h3>
            <LineChart labels={d.chart.map((c) => c.sale_date)} series={[{ name: "Revenue", values: d.chart.map((c) => c.revenue) }, { name: "Profit", values: d.chart.map((c) => c.profit) }]} />
          </section>
          <div className="grid2">
            <section className="panel card-pad"><h3>Top 10 products</h3>
              <table className="tbl"><thead><tr><th>Product</th><th className="num">Qty</th><th className="num">Revenue</th><th className="num">Profit</th></tr></thead>
                <tbody>{d.top_products.map((p) => <tr key={p.product_name}><td><b>{p.product_name}</b><div style={{ fontSize: ".72rem", color: "var(--muted)" }}>{p.manufacturer}</div></td>
                  <td className="num mono">{n0(p.total_qty)}</td><td className="num mono">{n0(p.revenue)}</td><td className="num mono">{n0(p.profit)}</td></tr>)}</tbody></table></section>
            <section className="panel card-pad"><h3>Top manufacturers</h3>
              <table className="tbl"><thead><tr><th>Manufacturer</th><th className="num">Sales</th><th className="num">Revenue</th><th className="num">Profit</th></tr></thead>
                <tbody>{d.manufacturers.map((m) => <tr key={m.manufacturer || "none"}><td><b>{m.manufacturer || "–"}</b></td><td className="num mono">{n0(m.sale_count)}</td>
                  <td className="num mono">{n0(m.revenue)}</td><td className="num mono">{n0(m.profit)}</td></tr>)}</tbody></table></section>
          </div>
          <section className="panel"><DataTable columns={columns} rows={d.rows} rowKey={(r) => r.sale_id} searchPlaceholder="Filter these sales" initialSort={{ key: "sale_date", dir: -1 }} empty="No sales in this period." /></section>
        </>
      )}
      {edit && (
        <Modal title={`Edit sale #${edit.sale_id}`} onClose={() => setEdit(null)}
          actions={<><button type="button" className="btn" onClick={() => setEdit(null)}>Cancel</button><button type="submit" form="se-form" className="btn primary">Save</button></>}>
          <form id="se-form" onSubmit={saveEdit} style={{ display: "grid", gap: ".7rem" }}>
            {edit.error && <div className="ui-notice err">{edit.error}</div>}
            <p style={{ margin: 0 }}>{edit.product_name}, {edit.sale_date}. Changing these figures doesn't change stock.</p>
            {[["sale_price", "Sale price", "0.01"], ["quantity_sold", "Quantity", "1"], ["total_amount", "Total amount", "0.01"], ["profit", "Profit", "0.01"]].map(([k, l, st]) => (
              <div className="field" key={k}><label htmlFor={`se-${k}`}>{l}</label><input id={`se-${k}`} type="number" step={st} required value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} /></div>))}
          </form>
        </Modal>
      )}
    </main>
  );
}
